import { BreakPointCondition, UI, useBreakpointIf, HStack, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Button } from 'native-base';
import React from 'react';
import { Dimensions, LayoutChangeEvent } from 'react-native';

import { ClosedMarketIndicator } from '../ClosedMarketIndicator';

import { getAlertText, getNoticeText, shouldBlockHistoricalSearch } from './constants';

import { FiltersTypes } from '.';

const specificPdfDownloadExists = false;

export const FiltersDesktop: React.FC<FiltersTypes.Props> = ({
  product,
  isExportingPdf,
  isToday,
  isInline,
  onDateSelected,
  onDownloadPdf,
  selectedDate,
  assetsFilterSelected,
  assetsFilters,
  assetsFiltersLoading,
  handleUpdateCustomerAssetsDetailsFiltersSelected,
}) => {
  const { width: windowWidth } = Dimensions.get('window');
  const [tabsMaxWidth, setTabsMaxWidth] = React.useState<number>(windowWidth);

  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const handleCalculateTabsMaxWidth = (event: LayoutChangeEvent) => {
    const spacingAsInt = Utils.Token.ToInt(Spacing, 'stack.xxxs');
    const spaceBetweenTabs = spacingAsInt;
    const windowPadding = spacingAsInt * 2;
    const containerWidth = windowWidth - windowPadding;
    const tabsContainerWidth = containerWidth - spaceBetweenTabs - event.nativeEvent.layout.width;
    setTabsMaxWidth(tabsContainerWidth);
  };

  const buttonDisabled = isExportingPdf || !specificPdfDownloadExists;

  const isHistoricalSearchBlocked = shouldBlockHistoricalSearch(product);

  if (!product) {
    return <></>;
  }

  return (
    <VStack space={Spacing['stack.xxs']} zIndex={5}>
      <HStack justifyContent="space-between">
        <HStack maxW={tabsMaxWidth}>
          <UI.Tabs
            testId="category-tabs"
            isDisabled={assetsFiltersLoading}
            isLoading={assetsFiltersLoading}
            activeTab={assetsFilterSelected}
            variant="secondary"
            onTabPress={handleUpdateCustomerAssetsDetailsFiltersSelected}
            tabs={assetsFilters.map((filter) => ({
              title: filter.label,
            }))}
          />
        </HStack>
        <HStack space={Spacing['inline.xxs']} onLayout={handleCalculateTabsMaxWidth}>
          <HStack space={Spacing['inline.xxxs']} alignItems="center">
            {isHistoricalSearchBlocked && (
              <UI.Notice
                testId="historical-search-blocked"
                type="alert"
                textObjects={getAlertText()}
              />
            )}
            {!isTablet && <ClosedMarketIndicator product={product} />}
            {!isToday && !!selectedDate && !isTablet && (
              <UI.Notice
                testId="notice-not-today"
                type="warning"
                backgroundColor={Utils.ColorToken.WithOpacity(
                  'feedback.attention.lightest',
                  'opacity.medium'
                )}
                textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
              />
            )}
            {!isInline && (
              <HStack alignItems="center" space={Spacing['inline.quarck']}>
                <UI.Icon name="Calendar" size="xxxs" color={Color['neutral.medium']} />
                <UI.Text color="neutral.medium">Posição por data:</UI.Text>
              </HStack>
            )}
            {!isInline && (
              <HStack maxW="150px">
                <UI.DatePicker
                  onClear={() => onDateSelected(undefined)}
                  onSelectDate={onDateSelected}
                  calendarPosition="right"
                  placeholder="Data atual"
                  isDisabled={isHistoricalSearchBlocked}
                  date={selectedDate}
                />
              </HStack>
            )}
          </HStack>
          {!isInline && (
            <HStack space={Spacing['inline.xxxs']}>
              <Button
                p={0}
                variant="textLink"
                size="lg"
                disabled={buttonDisabled}
                _text={{
                  color: Color[buttonDisabled ? 'neutral.light' : 'brand.tertiary.principal'],
                }}
                onPress={onDownloadPdf}
                rightIcon={
                  <UI.Icon
                    name="Pdf"
                    size="xs"
                    color={Color[buttonDisabled ? 'neutral.light' : 'brand.tertiary.principal']}
                  />
                }>
                EXPORTAR
              </Button>
            </HStack>
          )}
        </HStack>
      </HStack>
      {!isToday && !!selectedDate && !!isTablet && (
        <UI.Notice
          testId="notice-not-today"
          type="warning"
          backgroundColor={Utils.ColorToken.WithOpacity(
            'feedback.attention.lightest',
            'opacity.medium'
          )}
          textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
        />
      )}
      {isTablet && <ClosedMarketIndicator product={product} />}
    </VStack>
  );
};
